import { Link } from "react-router";
import { useState } from "react";
import { formatDuration } from "../../utils/timeFormatter";
import QuizModal from "../modals/QuizModal";
import { Pencil } from "lucide-react";

function QuizDisplayCard({ quiz, onQuizUpdated = () => {} }) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const ownerName = quiz.owner_detail
    ? `${quiz.owner_detail.user.first_name} ${quiz.owner_detail.user.last_name}`
    : "Admin";

  const quizUrl = `${window.location.origin}/quizzes/${quiz.id}/view`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(quizUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <>
      <div className="bg-base-100 rounded-lg shadow-sm hover:shadow transition-shadow duration-300 border border-gray-200 p-5 relative">
        {/* Top Right Edit Button */}
        <button
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100"
          onClick={() => setShowEditModal(true)}
          title="Edit Quiz"
        >
          <Pencil className="w-4 h-4 text-gray-600" />
        </button>

        {/* Title and Course Section */}
        <div className="mb-4">
          <h3 className="font-bold text-lg mb-1">{quiz.topic_detail.title}</h3>
          <div className="text-sm text-gray-600">
            Course: <span className="font-medium">{quiz.topic_detail.course_detail.title}</span>
          </div>
        </div>

        {/* Tags Section */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2 py-1 bg-gray-100 text-xs rounded-full font-medium">
            #{quiz.id.slice(0, 8)}
          </span>
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
            {quiz.quiz_type.toUpperCase()}
          </span>
          <span
            className={`px-2 py-1 text-xs rounded-full font-medium ${
              quiz.is_private
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {quiz.is_private ? "Private" : "Public"}
          </span>
          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
            Owner: {ownerName}
          </span>
          {quiz.expected_duration && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
              Duration: {formatDuration(quiz.expected_duration)}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end flex-wrap gap-2 pt-3 border-t border-gray-100">
          <Link to={`/manage/quizzes/${quiz.id}`} className="btn btn-sm btn-outline">
            Manage &gt;
          </Link>
          <Link to={`/manage/quizzes/${quiz.id}/responses`} className="btn btn-sm btn-outline btn-info">
            View Responses
          </Link>
          <button
            onClick={() => setShowShareModal(true)}
            className="btn btn-sm btn-outline btn-success"
            type="button"
          >
            Share
          </button>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-2">Share Quiz Link</h2>
            <div className="mb-4">
              <input type="text" value={quizUrl} readOnly className="input input-bordered w-full" />
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline" onClick={() => setShowShareModal(false)} type="button">
                Close
              </button>
              <button className="btn btn-primary" onClick={handleCopyLink} type="button">
                {copied ? "Copied!" : "Copy Link"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <QuizModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          currentQuiz={quiz}
          onSuccess={(updatedQuiz) => {
            onQuizUpdated(updatedQuiz);
            setShowEditModal(false);
          }}
        />
      )}
    </>
  );
}

export default QuizDisplayCard;